/**
 * Founder allowlist (frontend mirror of backend services/permissions/founders.ts).
 *
 * Platform owners must always have full access in the UI, even when the
 * backend RBAC calls (/rbac/my-permissions, /rbac/catalog) fail or are
 * rate-limited — otherwise an owner gets stuck on the "viewer" fallback.
 * The backend enforces the same allowlist on real API calls, so this only
 * affects what the UI shows/enables.
 */
import type { AccountInfo } from '@azure/msal-browser';

// White-label: override the founder allowlist per deployment with
// VITE_FOUNDER_EMAILS (comma-separated). When unset, the founding FNS
// addresses below apply so nothing changes for the current deployment.
const ENV_FOUNDER_EMAILS = (import.meta.env.VITE_FOUNDER_EMAILS as string | undefined)
  ?.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean);

const FOUNDER_EMAILS = new Set<string>(
  ENV_FOUNDER_EMAILS && ENV_FOUNDER_EMAILS.length > 0
    ? ENV_FOUNDER_EMAILS
    : [
        'esthermoise@gmail.com',
        'esther@frontlinenursestaffing.com',
        'noah@frontlinenursestaffing.com',
      ]
);

// Recover a real address from an Azure guest UPN like
// "esthermoise_gmail.com#EXT#@tenant.onmicrosoft.com".
function candidates(em: string): string[] {
  const lower = em.toLowerCase();
  const out = [lower];
  const ext = lower.indexOf('#ext#');
  if (ext > 0) {
    const local = lower.slice(0, ext);
    const u = local.lastIndexOf('_');
    if (u > 0) out.push(local.slice(0, u) + '@' + local.slice(u + 1));
  }
  return out;
}

export function isFounderEmail(em?: string | null): boolean {
  return !!em && candidates(em).some((c) => FOUNDER_EMAILS.has(c));
}

/** True if the signed-in MSAL account belongs to a founder. Checks every
 *  identity field the token might carry (email / preferred_username / upn /
 *  username) so a guest or .onmicrosoft UPN is still recognized. */
export function accountIsFounder(account?: AccountInfo | null): boolean {
  if (!account) return false;
  const claims = (account.idTokenClaims ?? {}) as {
    email?: string; preferred_username?: string; upn?: string;
  };
  const fields = [claims.email, claims.preferred_username, claims.upn, account.username];
  return fields.some((f) => isFounderEmail(f));
}
